import { InputState } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { MazeSystem } from './systems/MazeSystem';
import { PathFinding } from './systems/PathFinding';
import { Player, Direction } from './entities/Player';
import { Ghost, GhostType, GhostMode } from './entities/Ghost';

export class MazeRunnerGame extends BaseGame {
  private mazeSystem: MazeSystem;
  private pathFinding: PathFinding;
  private player: Player | null = null;
  private ghosts: Ghost[] = [];
  
  // Game state
  private gameStarted: boolean = false;
  private levelComplete: boolean = false;
  private powerPelletActive: boolean = false;
  private powerPelletTimer: number = 0;
  private readonly POWER_PELLET_DURATION = 8; // seconds
  private ghostsEaten: number = 0;
  
  // Level progression
  private levelStartDelay: number = 2;
  private gameOverDelay: number = 3;
  
  // Maze rendering offset (to center the maze)
  private mazeOffsetX: number = 0;
  private mazeOffsetY: number = 0;

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 600, height: 900, fps: 60, scale: 1 }; // Portrait for mobile
    super(renderManager, audioManager, gameConfig, 'maze-runner');
    
    // Initialize maze system
    this.mazeSystem = new MazeSystem(renderManager);
    this.pathFinding = new PathFinding(this.mazeSystem);
    
    // Calculate maze offset with minimal margins
    const mazePixelWidth = this.mazeSystem.getMazeWidth() * this.mazeSystem.getTileSize();
    const mazePixelHeight = this.mazeSystem.getMazeHeight() * this.mazeSystem.getTileSize();
    // Center horizontally, minimal top margin
    this.mazeOffsetX = Math.max(0, (this.config.width - mazePixelWidth) / 2);
    this.mazeOffsetY = 18; // Minimal top margin for HUD
  }

  public init(): void {
    console.log('Maze Runner Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = false;
    this.levelComplete = false;
    this.powerPelletActive = false;
    this.powerPelletTimer = 0;
    this.ghostsEaten = 0;
    this.levelStartDelay = 2;
    
    // Reset maze
    this.mazeSystem.reset();
    
    // Create player
    this.createPlayer();
    
    // Create ghosts
    this.createGhosts();
    
    // Start game after delay
    setTimeout(() => {
      this.gameStarted = true;
    }, this.levelStartDelay * 1000);
  }

  private createPlayer(): void {
    // Player starts at bottom center of maze
    const startX = 12; // Middle of 25-wide maze
    const startY = 18; // Near bottom
    
    this.player = new Player(
      'player',
      startX,
      startY,
      this.renderManager,
      this.audioManager,
      this.mazeSystem
    );
  }

  private createGhosts(): void {
    this.ghosts = [];
    
    // Ghost house coordinates (center of maze)
    const houseX = 12;
    const houseY = 10;
    
    // Create the four classic ghosts
    const ghostConfigs = [
      { type: GhostType.BLINKY, x: houseX, y: houseY - 3 }, // Start outside house
      { type: GhostType.PINKY, x: houseX, y: houseY },
      { type: GhostType.INKY, x: houseX - 1, y: houseY },
      { type: GhostType.CLYDE, x: houseX + 1, y: houseY }
    ];
    
    ghostConfigs.forEach((config, index) => {
      const ghost = new Ghost(
        `ghost_${config.type}`,
        config.type,
        config.x,
        config.y,
        this.renderManager,
        this.audioManager,
        this.mazeSystem,
        this.pathFinding
      );
      this.ghosts.push(ghost);
    });
  }

  public update(deltaTime: number): void {
    if (this.isGameOver) {
      this.gameOverDelay -= deltaTime;
      return;
    }
    
    if (this.levelComplete) {
      this.handleLevelComplete();
      return;
    }
    
    if (!this.gameStarted || !this.player) return;
    
    // Update power pellet timer
    this.updatePowerPellet(deltaTime);
    
    // Update player
    if (this.player.active) {
      const collectResult = this.player.update(deltaTime);
      
      // Check if power pellet was consumed
      if (collectResult.powerPellet) {
        console.log('Power pellet consumed!');
        this.activatePowerPellet();
      }
    }
    
    // Update ghosts
    this.updateGhosts(deltaTime);
    
    // Check collisions
    this.checkCollisions();
    
    // Check win condition
    const dotsRemaining = this.mazeSystem.getRemainingDots();
    if (dotsRemaining === 0) {
      console.log('All dots collected! Level complete.');
      this.levelComplete = true;
    }
  }

  private updatePowerPellet(deltaTime: number): void {
    if (this.powerPelletActive) {
      this.powerPelletTimer -= deltaTime;
      
      if (this.powerPelletTimer <= 0) {
        this.powerPelletActive = false;
        this.ghostsEaten = 0;
        
        // Return ghosts to chase mode
        this.ghosts.forEach(ghost => {
          if (ghost.getMode() === GhostMode.FRIGHTENED) {
            ghost.setMode(GhostMode.CHASE);
          }
        });
      }
    }
  }


  private activatePowerPellet(): void {
    this.powerPelletActive = true;
    this.powerPelletTimer = this.POWER_PELLET_DURATION;
    this.ghostsEaten = 0;
    
    // Make all active ghosts frightened
    this.ghosts.forEach(ghost => {
      if (ghost.getMode() !== GhostMode.IN_HOUSE && ghost.getMode() !== GhostMode.EATEN) {
        ghost.setMode(GhostMode.FRIGHTENED, this.POWER_PELLET_DURATION);
      }
    });
    
    this.audioManager.playPowerUp();
  }

  private updateGhosts(deltaTime: number): void {
    if (!this.player) return;
    
    const playerPos = this.player.getGridPosition();
    
    this.ghosts.forEach((ghost, index) => {
      ghost.setPlayerPosition(playerPos);
      ghost.update(deltaTime);
      
      // Debug: log ghost positions occasionally
      if (Math.floor(Date.now() / 1000) % 5 === 0 && index === 0) {
        const ghostPos = ghost.getGridPosition();
        console.log(`Ghost ${ghost.getType()} at (${ghostPos.x}, ${ghostPos.y}), Player at (${playerPos.x}, ${playerPos.y}), Mode: ${ghost.getMode()}`);
      }
    });
  }

  private checkCollisions(): void {
    if (!this.player || !this.player.active) return;
    
    const playerPos = this.player.getGridPosition();
    
    this.ghosts.forEach(ghost => {
      if (!ghost.active) return;
      
      const ghostPos = ghost.getGridPosition();
      
      // Check if player and ghost are in the same grid position
      if (playerPos.x === ghostPos.x && playerPos.y === ghostPos.y) {
        if (ghost.isVulnerable()) {
          // Player eats ghost
          this.eatGhost(ghost);
        } else if (ghost.getMode() !== GhostMode.EATEN) {
          // Ghost catches player
          this.playerCaught();
        }
      }
    });
  }

  private eatGhost(ghost: Ghost): void {
    if (!this.player) return;
    
    const basePoints = 200;
    const multiplier = Math.pow(2, this.ghostsEaten);
    const points = basePoints * multiplier;
    
    this.addScore(points);
    this.ghostsEaten++;
    
    ghost.setMode(GhostMode.EATEN);
    this.audioManager.playPowerUp();
    
    // Show score popup (simplified)
    console.log(`Ghost eaten! +${points} points`);
  }

  private playerCaught(): void {
    if (!this.player || this.player.isInvulnerable()) return;
    
    this.player.destroy();
    this.loseLife();
    
    if (!this.isGameOver) {
      // Respawn player after delay
      setTimeout(() => {
        if (!this.isGameOver && this.player) {
          this.player.reset(12, 18);
          this.player.setInvulnerable(2); // 2 seconds invulnerability
        }
      }, 1000);
    }
  }

  private handleLevelComplete(): void {
    console.log(`Level ${this.level} complete! Moving to level ${this.level + 1}`);
    
    // Start next level
    this.nextLevel();
    this.levelComplete = false;
    this.powerPelletActive = false;
    this.powerPelletTimer = 0;
    this.ghostsEaten = 0;
    
    // Reset maze
    this.mazeSystem.reset();
    
    // Reset player
    if (this.player) {
      this.player.reset(12, 18);
    }
    
    // Reset ghosts
    this.createGhosts();
    
    // Brief pause before starting
    this.gameStarted = false;
    this.levelStartDelay = 1;
    setTimeout(() => {
      this.gameStarted = true;
    }, this.levelStartDelay * 1000);
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen
    this.renderManager.clear('#000000');
    
    // Render maze
    this.mazeSystem.render(ctx, this.mazeOffsetX, this.mazeOffsetY);
    
    // Render player
    if (this.player && this.player.active) {
      // Adjust player position for maze offset
      const originalPos = this.player.position;
      this.player.position = {
        x: originalPos.x + this.mazeOffsetX,
        y: originalPos.y + this.mazeOffsetY
      };
      this.player.render(ctx);
      this.player.position = originalPos; // Restore original position
    }
    
    // Render ghosts
    this.ghosts.forEach(ghost => {
      if (ghost.active) {
        // Adjust ghost position for maze offset
        const originalPos = ghost.position;
        ghost.position = {
          x: originalPos.x + this.mazeOffsetX,
          y: originalPos.y + this.mazeOffsetY
        };
        ghost.render(ctx);
        ghost.position = originalPos; // Restore original position
      }
    });
    
    // Render HUD
    this.renderHUD();
    
    // Render game state messages
    this.renderGameStateMessages();
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      console.log('MazeRunnerGame: Rendering initial entry component');
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  protected renderHUD(): void {
    // Player score
    const playerScore = this.player ? this.player.getScore() + this.score : this.score;
    this.renderManager.drawText(
      `SCORE: ${playerScore.toString().padStart(6, '0')}`,
      20,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Lives
    this.renderManager.drawText('LIVES: ', this.config.width - 150, 20, '#FFFFFF', '16px "Courier New", monospace');
    for (let i = 0; i < this.lives; i++) {
      this.renderManager.drawC64Char('●', this.config.width - 80 + i * 20, 18, '#BFCE72', 1);
    }
    
    // Level
    this.renderManager.drawTextCentered(
      `LEVEL ${this.level}`,
      this.config.width / 2,
      20,
      '#BFCE72',
      '16px "Courier New", monospace'
    );
    
    // Dots remaining
    const dotsLeft = this.mazeSystem.getRemainingDots();
    this.renderManager.drawText(
      `DOTS: ${dotsLeft}`,
      20,
      this.config.height - 30,
      '#787878',
      '12px "Courier New", monospace'
    );
    
    // Power pellet timer
    if (this.powerPelletActive) {
      const timeLeft = Math.ceil(this.powerPelletTimer);
      this.renderManager.drawTextCentered(
        `POWER: ${timeLeft}`,
        this.config.width / 2,
        this.config.height - 30,
        '#BFCE72',
        '14px "Courier New", monospace'
      );
    }
  }

  private renderGameStateMessages(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    if (!this.gameStarted && !this.isGameOver) {
      // Level start message
      this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
      this.renderManager.drawTextCentered(
        `LEVEL ${this.level}`,
        centerX,
        centerY - 20,
        '#BFCE72',
        '32px "Courier New", monospace'
      );
      this.renderManager.drawTextCentered(
        'GET READY!',
        centerX,
        centerY + 20,
        '#FFFFFF',
        '18px "Courier New", monospace'
      );
    }
    
    if (this.levelComplete) {
      this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
      this.renderManager.drawTextCentered(
        'LEVEL COMPLETE!',
        centerX,
        centerY,
        '#55A049',
        '24px "Courier New", monospace'
      );
    }
    
    if (this.isGameOver) {
      this.renderGameOver();
    }
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      // Don't handle input if showing initial entry
      if (this.showingInitialEntry) {
        return;
      }
      
      // Handle game over input
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }
    
    if (!this.player || !this.gameStarted) return;
    
    // Handle player movement input
    if (input.keys['ArrowUp'] || input.keys['KeyW']) {
      this.player.setNextDirection(Direction.UP);
    } else if (input.keys['ArrowDown'] || input.keys['KeyS']) {
      this.player.setNextDirection(Direction.DOWN);
    } else if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      this.player.setNextDirection(Direction.LEFT);
    } else if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      this.player.setNextDirection(Direction.RIGHT);
    }
    
    // Handle touch input with swipe gestures
    if (input.touch.gesture === 'swipe') {
      const swipeDir = this.getSwipeDirection(input);
      if (Math.abs(swipeDir.x) > Math.abs(swipeDir.y)) {
        // Horizontal swipe
        if (swipeDir.x > 0) {
          this.player.setNextDirection(Direction.RIGHT);
        } else {
          this.player.setNextDirection(Direction.LEFT);
        }
      } else {
        // Vertical swipe
        if (swipeDir.y > 0) {
          this.player.setNextDirection(Direction.DOWN);
        } else {
          this.player.setNextDirection(Direction.UP);
        }
      }
    }
  }

  private getSwipeDirection(input: InputState): { x: number, y: number } {
    const deltaX = input.touch.position.x - input.touch.startPosition.x;
    const deltaY = input.touch.position.y - input.touch.startPosition.y;
    const magnitude = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    if (magnitude > 0) {
      return { x: deltaX / magnitude, y: deltaY / magnitude };
    }
    
    return { x: 0, y: 0 };
  }

  public restart(): void {
    super.restart();
    this.gameStarted = false;
    this.levelComplete = false;
    this.powerPelletActive = false;
    this.powerPelletTimer = 0;
    this.ghostsEaten = 0;
    this.levelStartDelay = 2;
    this.gameOverDelay = 3;
  }

  public cleanup(): void {
    super.cleanup();
    this.player = null;
    this.ghosts = [];
  }
}